// LayerThumbnailPreference — 「レイヤーのサムネイル」を出すかの per-user 設定を
// localStorage に永続化する infrastructure 層ユーティリティ
// (layer-thumbnails-2026-10-13.md「ユーザー設定でサムネを OFF にできる」)。
//
// 既定は ON。OFF = レイヤー欄は番号だけ (beta10 と同じ見た目) + エンジン側のサムネ処理も
// 止める。端末ローカルのみ・サーバ同期なし・protocol 変更なし。
// 設計は showOwnCustomCursorPreference.ts と同流儀 (parse 失敗 / 不在 / storage 不可は既定へ、
// 例外を throw しない)。

export const LAYER_THUMBNAILS_KEY = 'oekaki:layer-thumbnails';

/** 既定値 (= ON)。 */
export const DEFAULT_LAYER_THUMBNAILS = true;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LayerThumbnailPreferenceStore {
  /** サムネを出すか。不在 / 不正 / storage 不可 → 既定 (ON)。 */
  get(): boolean;
  /** 保存。storage 不可なら no-op。 */
  set(enabled: boolean): void;
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:layer-thumbnails__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export const createLayerThumbnailPreferenceStore = (
  opts: { storage?: StorageLike | null } = {},
): LayerThumbnailPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());
  return {
    get() {
      if (storage === null) return DEFAULT_LAYER_THUMBNAILS;
      try {
        const raw = storage.getItem(LAYER_THUMBNAILS_KEY);
        if (raw === 'on') return true;
        if (raw === 'off') return false;
      } catch {
        // fallthrough
      }
      return DEFAULT_LAYER_THUMBNAILS;
    },
    set(enabled) {
      if (storage === null) return;
      try {
        storage.setItem(LAYER_THUMBNAILS_KEY, enabled ? 'on' : 'off');
      } catch {
        // QuotaExceeded 等 — 永続化できないだけで動作は継続
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(LAYER_THUMBNAILS_KEY);
      } catch {
        // ignore
      }
    },
  };
};
